import React, { useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useInView } from 'react-intersection-observer';
import Link from 'components/shared/link';
import Button from '../../../../shared/button';
import styles from './styles.module.scss';

// Separate Card component to properly handle hooks
const Card = ({
  card,
  imageFit = 'cover',
  textsAlligment = 'left',
  imageHeight = undefined,
  imageBoxBackground = null,
}) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  // Create a ref for the image container
  const imageContainerRef = useRef(null);

  // Function to handle image appending
  const appendImageToCard = useCallback((cardId) => {
    const imageDiv = document.getElementById(`img-${cardId}`);
    if (imageDiv && imageContainerRef.current) {
      imageContainerRef.current.appendChild(imageDiv);
    }
  }, []);

  // Handle image appending for this specific card
  useEffect(() => {
    if (card.image?.id) {
      appendImageToCard(card.image.id);
    }
  }, [card.image?.id, appendImageToCard]);

  const Element = useMemo(() => (card.buttonLink ? Link : 'div'), [card]);

  return (
    <div
      ref={ref}
      className={cn(styles.card, {
        [styles.visible]: inView,
        [styles.isLink]: !!card.buttonLink,
      })}
    >
      <Element to={card.buttonLink}>
        <div
          ref={imageContainerRef}
          className={cn(styles.imageContainer, { [styles.contain]: imageFit === 'contain' })}
          style={{
            height: imageHeight || undefined,
            backgroundColor: imageBoxBackground || undefined,
          }}
        />
        <div
          className={cn(styles.content, styles[textsAlligment], {
            [styles.reduceTopPadding]: imageFit === 'contain',
          })}
        >
          {card.title && <h3 className={styles.title}>{card.title}</h3>}
          {card.description && <p className={styles.description}>{card.description}</p>}
          {card.buttonLabel && card.buttonLink && (
            <div className={styles.buttonContainer}>
              <Button theme="accent-primary" to={card.buttonLink} size="sm">
                {card.buttonLabel}
              </Button>
            </div>
          )}
        </div>
      </Element>
    </div>
  );
};

Card.propTypes = {
  card: PropTypes.shape({
    image: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }).isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    buttonLabel: PropTypes.string,
    buttonLink: PropTypes.string,
  }).isRequired,
  imageFit: PropTypes.oneOf(['cover', 'contain']),
  textsAlligment: PropTypes.oneOf(['left', 'center']),
  imageHeight: PropTypes.number,
  imageBoxBackground: PropTypes.string,
};

export default Card;
